import { getDocs, query, where } from 'firebase/firestore'
import { getTenantContextInstance as tenantCtx } from '@/plugins/tenant'

/**
 * Shared connector definition (admin/_shared/connectors) whose `supports` apply
 * to every tenant custom-integration:* connector.
 */
const CUSTOM_INTEGRATION_KEY = 'custom-integration'
const CUSTOM_INTEGRATION_PREFIX = `${CUSTOM_INTEGRATION_KEY}:`

const toConnectorKey = identifier => {
  if (!identifier) return null

  return identifier.startsWith(CUSTOM_INTEGRATION_PREFIX)
    ? identifier
    : `${CUSTOM_INTEGRATION_PREFIX}${identifier}`
}

const isUsableCustomIntegration = ({
  identifier,
  method,
  useEnvironments,
  production,
  sandbox,
}) => {
  if (!identifier) return false

  return useEnvironments
    ? [production?.method, sandbox?.method].some(item => item && item !== 'GET')
    : Boolean(method && method !== 'GET')
}

export default {
  namespaced: true,
  state: {
    list: [],
    customConnectors: [],
  },
  getters: {
    // Capabilities shared by every custom integration live on the single
    // `custom-integration` definition and are resolved on read — never copied
    // onto each connector at fetch time.
    customConnectorSupports: state => state.list.find(el => el.key === CUSTOM_INTEGRATION_KEY)?.supports ?? [],
    getCustomConnectors: (state, getters) => state.customConnectors.map(connector => ({
      ...connector,
      supports: getters.customConnectorSupports,
    })),
    getAll: (state, getters) => [
      ...state.list.filter(el => el.key !== CUSTOM_INTEGRATION_KEY),
      ...getters.getCustomConnectors,
    ],
    getByKey: (state, getters) => key => getters.getAll.find(el => el.key === key),
    getMappingsConnectors: (state, getters) => getters.getAll.filter(el => el.supports?.includes('mappings')),
    getPrefillsConnectors: (state, getters) => getters.getAll.filter(el => el.supports?.includes('prefills')),
    getInstructionsConnectors: (state, getters) => getters.getAll.filter(el => el.supports?.includes('instructions')),
  },
  mutations: {
    UPDATE_LIST(state, payload) {
      state.list = payload
    },
    UPDATE_CUSTOM_CONNECTORS(state, payload) {
      state.customConnectors = payload
    },
  },
  actions: {
    /**
     * Fetches all connector definitions from admin/_shared/connectors.
     *
     * @param {Object} Object.commit
     *
     * @returns {Promise}
     */
    fetchAll({ commit }) {
      return new Promise((resolve, reject) => {
        const { sharedConnectors } = tenantCtx()

        getDocs(sharedConnectors)
          .then(querySnapshot => {
            const data = JSON.parse(
              JSON.stringify(
                querySnapshot.docs.map(
                  docRef => docRef.data(),
                ),
              ),
            )

            commit('UPDATE_LIST', data)
            resolve(data)
          })
          .catch(error => reject(error))
      })
    },

    /**
     * Fetches the tenant's custom integrations. Their supported capabilities are
     * resolved from the shared custom-integration definition via the getters.
     *
     * @param {Object} Object.commit
     *
     * @returns {Promise}
     */
    fetchCustomConnectors({ commit }) {
      return new Promise((resolve, reject) => {
        const { integrations } = tenantCtx()

        const q = query(
          integrations,
          where('type', '==', 'custom'),
        )

        getDocs(q)
          .then(querySnapshot => {
            const data = JSON.parse(
              JSON.stringify(
                querySnapshot.docs
                  .map(docRef => {
                    const integration = docRef.data()

                    if (!isUsableCustomIntegration(integration)) {
                      return null
                    }

                    return {
                      key: toConnectorKey(integration.identifier),
                      label: integration.name,
                      type: integration.type,
                    }
                  })
                  .filter(Boolean),
              ),
            )

            commit('UPDATE_CUSTOM_CONNECTORS', data)
            resolve(data)
          })
          .catch(error => {
            console.log(error)
            reject(error)
          })
      })
    },
  },
}
